// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
// La RÈGLE DE CODIFICATION des paramètres et des examens.
//
// ── Pourquoi une règle, et pourquoi celle-ci ────────────────────────────────
//
// Le code d'un paramètre ou d'un examen n'est pas un libellé : c'est une CLÉ.
// Il voyage dans les liens entre fiches, dans les adresses partageables, et
// jusque dans les clés du journal des cartes de contrôle (« CQI-AST-EQ01 »).
// Un code qui change casse ces liens ; un code ambigu en fabrique de faux.
//
// La règle ci-dessous n'est pas un idéal théorique : elle est relevée sur les
// données réelles du laboratoire (85 paramètres, 310 examens, 18/08/2026).
//   · aucun code ne contient d'accent, d'espace ni de ponctuation — on fige
//     cet acquis plutôt que de le laisser se perdre ;
//   · les codes vont de 1 à 21 caractères, mais tout ce qui dépasse 14 vient
//     de l'import automatique du livret (`ELECTRO_PROTEIN`, `ACTIVIT_XA_ACT_2`)
//     et se lit mal ; 14 est donc la borne ;
//   · `17OHP` commence par un chiffre et c'est LÉGITIME (17-hydroxyprogestérone) :
//     exiger une lettre en tête rejetterait un code juste.
//
// ── Le code est PARTAGÉ entre les deux faces ────────────────────────────────
//
// Un paramètre commandable seul EST un examen : les deux portent alors le même
// code, et c'est ce code qui les identifie comme un seul objet. L'unicité se
// vérifie donc par famille (deux paramètres ne peuvent pas partager un code,
// deux examens non plus) mais un paramètre et un examen le peuvent — et
// doivent, quand ils sont la même chose.

export const CODE_MIN = 2;
export const CODE_MAX = 14;

/** Les règles, telles qu'elles s'affichent à l'utilisateur au moment où il
 *  saisit un code. Écrites ici et NON dans l'écran : les deux écrans de
 *  création (paramètre, examen) doivent dire exactement la même chose, et deux
 *  textes recopiés divergent au premier ajustement. */
export const REGLES_CODE: string[] = [
  `${CODE_MIN} à ${CODE_MAX} caractères`,
  'majuscules, chiffres, tiret et tiret bas — ni accent, ni espace, ni point',
  'unique : deux paramètres ne peuvent pas le partager, deux examens non plus',
  'même code = même objet : un paramètre commandable seul et son examen le partagent',
  'un code ne se change plus une fois attribué — il voyage dans les liens et les journaux',
  'un SIGLE, jamais un mot coupé : « NFS », « TSH », « CRP » — pas « ADENOVI », pas « CELLULE »',
];

/**
 * Le code coupe-t-il un MOT en plein milieu ?
 *
 * ── Le défaut que ceci nomme ────────────────────────────────────────────────
 *
 * Le générateur de l'import du livret tronquait chaque mot à SEPT lettres :
 * `ADENOVI_DANS` pour « Adénovirus dans les selles », `CELLULE_PARIETA`,
 * `CRYPTOC_NEOFORM`, `IDENTIF_BACTERI`. RQ, 18/08/2026 : « ADENOVI DANS est
 * inacceptable pour moi ».
 *
 * Un code n'a pas à être un mot entier — `NFS`, `TSH`, `CRP` sont des sigles et
 * c'est ce qu'on veut. Ce qui est refusé, c'est le mot AMPUTÉ : sept lettres
 * pile, qui ne forment ni un sigle ni un mot. On ne peut pas le prouver par une
 * règle, mais on peut le SIGNALER pendant la saisie — d'où un avertissement, et
 * non un refus : c'est un doute, pas une faute.
 */
export function motTronque(code: string): string[] {
  return String(code || '').split(/[_-]/)
    .filter((m) => m.length === 7 && /^[A-Z]+$/.test(m))
    // Les sigles et abréviations légitimes de sept lettres, nommés une fois.
    .filter((m) => !['MULTIPL', 'COMPACT', 'GENEXPE'].includes(m));
}

export interface VerdictCode { valide: boolean; motif: string }

/** Le code est-il conforme ? Le motif est destiné à être AFFICHÉ tel quel. */
export function verifierCode(code: string): VerdictCode {
  const c = String(code || '').trim();
  if (!c) return { valide: false, motif: 'Le code est obligatoire.' };
  if (c.length < CODE_MIN) return { valide: false, motif: `Trop court — ${CODE_MIN} caractères au minimum.` };
  if (c.length > CODE_MAX) {
    return { valide: false, motif: `Trop long — ${CODE_MAX} caractères au maximum (actuellement ${c.length}).` };
  }
  if (!/^[A-Z0-9][A-Z0-9_-]*$/.test(c)) {
    return {
      valide: false,
      motif: 'Majuscules, chiffres, tiret et tiret bas uniquement — ni accent, ni espace, ni point.',
    };
  }
  return { valide: true, motif: '' };
}

/** Normalise une saisie SANS la censurer : on met en majuscules et on remplace
 *  les séparateurs courants, mais on ne coupe pas et on ne supprime pas les
 *  caractères refusés — l'utilisateur doit VOIR ce qui coince plutôt que de
 *  voir sa frappe disparaître sous ses doigts. */
export function normaliserCode(saisie: string): string {
  return String(saisie || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[ .]/g, '_');
}
